import React, { useEffect, Suspense } from "react";
// lazyWithRetry = React.lazy + one guarded reload on post-deploy chunk-load
// failures (stale index.html pointing at deleted hashed chunks).
import { lazyWithRetry } from "./utils/functions/lazyWithRetry";
import { registerRoutePreloads } from "./utils/functions/routePreload";
import LoadingElement from "./presentation/components/loading-element/loading-element";
import ReactDOM from "react-dom/client";
import "./index.css";
import { InteractionStatus, PublicClientApplication } from "@azure/msal-browser";
import { msalConfig } from "./AuthConfig";
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
} from "react-router-dom";
import { isMigrationHost } from "./utils/functions/isMigrationHost";
import { usePageViewTracking } from "./hooks/usePageViewTracking";
import { useClarityConsentPageView } from "./hooks/useClarityConsentPageView";
const StyleListScreen = lazyWithRetry(() => import("./presentation/screens/style-list/style-list.screen").then((m) => ({ default: m.StyleListScreen })));
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
const MaskEditorScreen = lazyWithRetry(() => import("./presentation/screens/style-mask-editor/MaskEditorScreen").then((m) => ({ default: m.MaskEditorScreen })));
import { PrivateView, PublicView } from "./App";
import { MsalProvider, useMsal } from "@azure/msal-react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { DesignListScreen } from "./presentation/screens/design-list/design-list.screen";
const CreateDesignScreen = lazyWithRetry(() => import("./presentation/screens/design-create/create-design.screen").then((m) => ({ default: m.CreateDesignScreen })));
import { OrderListScreen } from "./presentation/screens/order-list/order-list.screen";
import { LogoListScreen } from "./presentation/screens/logo-list/logo-list.screen";
const StyleCatalogUploadScreen = lazyWithRetry(() => import("./presentation/screens/style-catalog-upload/StyleCatalogUploadScreen"));
//@ts-ignore
import "./i18n.ts";
import AuthRoute from "./routing/AuthRoute";
import CartRoute from "./routing/CartRoute";
import { Role } from "./core/models/entities/Role";
import { enviroment } from "./enviroment";
const StyleEditScreen = lazyWithRetry(() => import("./presentation/screens/style-edit/StyleEditScreen"));
const LogoEditorScreen = lazyWithRetry(() => import("./presentation/screens/logo-editor/logo-editor.screen"));
import {
  createTheme,
  CssBaseline,
  StyledEngineProvider,
  ThemeProvider,
} from "@mui/material";
const OrderCreateScreen = lazyWithRetry(() => import("./presentation/screens/order-create/OrderCreateScreen").then((m) => ({ default: m.OrderCreateScreen })));
const OrderPlaced = lazyWithRetry(() => import("./presentation/screens/order-placed/OrderPlaced").then((m) => ({ default: m.OrderPlaced })));
const CustomerListScreen = lazyWithRetry(() => import("./presentation/screens/customer-list/customer-list.screen").then((m) => ({ default: m.CustomerListScreen })));
const CustomerCreateScreen = lazyWithRetry(() => import("./presentation/screens/customer-create/customer-create.screen").then((m) => ({ default: m.CustomerCreateScreen })));
const CustomerUpdateScreen = lazyWithRetry(() => import("./presentation/screens/customer-update/customer-update.screen").then((m) => ({ default: m.CustomerUpdateScreen })));
const VendorUpdateScreen = lazyWithRetry(() => import("./presentation/screens/customer-update/customer-update.screen").then((m) => ({ default: m.VendorUpdateScreen })));
import reportWebVitals from "./reportWebVitals";
import { reportWebVital, scheduleWebVitals } from "./utils/analytics/webVitals";
const MyProfileScreen = lazyWithRetry(() => import("./presentation/screens/my-profile/my-profile.screen").then((m) => ({ default: m.MyProfileScreen })));
const DesignEditor2Screen = lazyWithRetry(() => import("./presentation/screens/design-editor2/design-editor2.screen").then((m) => ({ default: m.DesignEditor2Screen })));
import { Outlet } from "react-router";
const VendorListScreen = lazyWithRetry(() => import("./presentation/screens/vendor-list/vendor-list.screen").then((m) => ({ default: m.VendorListScreen })));
const ManufacturerListScreen = lazyWithRetry(() => import("./presentation/screens/manufacturer-list/manufacturer-list.screen").then((m) => ({ default: m.ManufacturerListScreen })));
const ManufacturerDetailsScreen = lazyWithRetry(() => import("./presentation/screens/manufacturer-details/manufacturer-details.screen").then((m) => ({ default: m.ManufacturerDetailsScreen })));
const CustomerDetailsScreen = lazyWithRetry(() => import("./presentation/screens/customer-details/customer-details.screen").then((m) => ({ default: m.CustomerDetailsScreen })));
const VendorDetailsScreen = lazyWithRetry(() => import("./presentation/screens/customer-details/customer-details.screen").then((m) => ({ default: m.VendorDetailsScreen })));
const OrderConfirmationScreen = lazyWithRetry(() => import("./presentation/screens/order-confirmation/OrderConfirmationScreen").then((m) => ({ default: m.OrderConfirmationScreen })));
import { OrderConfirmErrorRedirect } from "./presentation/screens/order-confirmation/OrderConfirmErrorRedirect";
const DesignChangeStyleScreen = lazyWithRetry(() => import("./presentation/screens/design-update-style/design-change-style.screen").then((m) => ({ default: m.DesignChangeStyleScreen })));
const OrderDetailsScreen = lazyWithRetry(() => import("./presentation/screens/order-details/OrderDetailsScreen").then((m) => ({ default: m.OrderDetailsScreen })));
const ExternalCustomerDesignEditor = lazyWithRetry(() => import("./presentation/screens/public/ExternalCustomerDesignEditor"));
const ExternalDesignEditorScreen = lazyWithRetry(() => import("./presentation/screens/public/ExternalDesignEditorScreen").then((m) => ({ default: m.ExternalDesignEditorScreen })));
const ExternalStyleUploadScreen = lazyWithRetry(() => import("./presentation/screens/public/ExternalStyleUploadScreen").then((m) => ({ default: m.ExternalStyleUploadScreen })));
import { WhiteLabelProvider } from "./WhiteLabelContext";
const ExternalApprovalDesignScreen = lazyWithRetry(() => import("./presentation/screens/public/ExternalApprovalDesignScreen").then((m) => ({ default: m.ExternalApprovalDesignScreen })));
const ExternalApprovalLogoScreen = lazyWithRetry(() => import("./presentation/screens/public/ExternalApprovalLogoScreen").then((m) => ({ default: m.ExternalApprovalLogoScreen })));
const MultiApprovalRouter = lazyWithRetry(() => import("./presentation/screens/public/MultiApprovalRouter").then((m) => ({ default: m.MultiApprovalRouter })));
const MigrationPage = lazyWithRetry(() => import("./presentation/screens/public/MigrationPage").then((m) => ({ default: m.MigrationPage })));
const VelkommenPage = lazyWithRetry(() => import("./presentation/screens/public/VelkommenPage").then((m) => ({ default: m.VelkommenPage })));
const ProofPdfDownloadScreen = lazyWithRetry(() => import("./presentation/screens/public/ProofPdfDownloadScreen").then((m) => ({ default: m.ProofPdfDownloadScreen })));
const OrganizationDisabledScreen = lazyWithRetry(() => import("./presentation/screens/organization-disabled/OrganizationDisabledScreen"));
const PriceImportScreen = lazyWithRetry(() => import("./presentation/screens/admin-price-import/price-import.screen").then((m) => ({ default: m.PriceImportScreen })));
const LogoImportScreen = lazyWithRetry(() => import("./presentation/screens/admin-logo-import/logo-import.screen").then((m) => ({ default: m.LogoImportScreen })));
const DesignImportScreen = lazyWithRetry(() => import("./presentation/screens/admin-design-import/design-import.screen").then((m) => ({ default: m.DesignImportScreen })));
const StatisticsScreen = lazyWithRetry(() => import("./presentation/screens/admin-statistics/statistics.screen").then((m) => ({ default: m.StatisticsScreen })));
const Co2CalculatorScreen = lazyWithRetry(() => import("./presentation/screens/admin-co2/co2-calculator.screen").then((m) => ({ default: m.Co2CalculatorScreen })));
const ApiKeysListScreen = lazyWithRetry(() => import("./presentation/screens/admin-api-keys/ApiKeysListScreen").then((m) => ({ default: m.ApiKeysListScreen })));
const ApiKeyDetailScreen = lazyWithRetry(() => import("./presentation/screens/admin-api-keys/ApiKeyDetailScreen").then((m) => ({ default: m.ApiKeyDetailScreen })));
const OrdersQueueScreen = lazyWithRetry(() => import("./presentation/screens/admin-orders-queue/orders-queue.screen").then((m) => ({ default: m.OrdersQueueScreen })));
const MethodsScreen = lazyWithRetry(() => import("./presentation/screens/admin-methods/methods.screen").then((m) => ({ default: m.MethodsScreen })));
const StockOrdersScreen = lazyWithRetry(() => import("./presentation/screens/admin-stock-orders/stock-orders.screen").then((m) => ({ default: m.StockOrdersScreen })));
const PrepaymentsScreen = lazyWithRetry(() => import("./presentation/screens/admin-prepayments/prepayments.screen").then((m) => ({ default: m.PrepaymentsScreen })));
const OrdersTodayScreen = lazyWithRetry(() => import("./presentation/screens/admin-orders-today/orders-today.screen").then((m) => ({ default: m.OrdersTodayScreen })));
const ImportStatusScreen = lazyWithRetry(() => import("./presentation/screens/admin-import-status/import-status.screen").then((m) => ({ default: m.ImportStatusScreen })));
const SystemHealthScreen = lazyWithRetry(() => import("./presentation/screens/admin-system-health/system-health.screen").then((m) => ({ default: m.SystemHealthScreen })));
const BillingOverviewScreen = lazyWithRetry(() => import("./presentation/screens/admin-billing-overview/billing-overview.screen").then((m) => ({ default: m.BillingOverviewScreen })));
const VendorUsersScreen = lazyWithRetry(() => import("./presentation/screens/admin-vendor-users/vendor-users.screen").then((m) => ({ default: m.VendorUsersScreen })));
const StripePayoutsScreen = lazyWithRetry(() => import("./presentation/screens/admin-stripe-payouts/stripe-payouts.screen").then((m) => ({ default: m.StripePayoutsScreen })));
const StripePayoutDetailScreen = lazyWithRetry(() => import("./presentation/screens/admin-stripe-payouts/stripe-payout-detail.screen").then((m) => ({ default: m.StripePayoutDetailScreen })));
const NavPriceFallbackScreen = lazyWithRetry(() => import("./presentation/screens/admin-nav-price-fallback/nav-price-fallback.screen").then((m) => ({ default: m.NavPriceFallbackScreen })));
const MailTestScreen = lazyWithRetry(() => import("./presentation/screens/admin-mail-test/mail-test.screen").then((m) => ({ default: m.MailTestScreen })));
const MailDeliveryScreen = lazyWithRetry(() => import("./presentation/screens/admin-mail-delivery/mail-delivery.screen").then((m) => ({ default: m.MailDeliveryScreen })));
const ApprovalMailsScreen = lazyWithRetry(() => import("./presentation/screens/admin-approval-mails/approval-mails.screen").then((m) => ({ default: m.ApprovalMailsScreen })));
const SheetSizeScreen = lazyWithRetry(() => import("./presentation/screens/admin-sheet-size/sheet-size.screen").then((m) => ({ default: m.SheetSizeScreen })));

// Admin perf FE-1 (flag REACT_APP_ADMIN_PERF_ROUTE_SUSPENSE_ENABLED): the
// menu paths whose screen chunk the admin dropdown / main nav may warm on
// open/hover. Registration only (a Map insert) - nothing is downloaded until
// a flag-gated caller asks. The route table below is unchanged.
registerRoutePreloads({
  "/style": StyleListScreen,
  "/customer": CustomerListScreen,
  "/vendor": VendorListScreen,
  "/manufacturer": ManufacturerListScreen,
  "/admin/statistics": StatisticsScreen,
  "/admin/orders-today": OrdersTodayScreen,
  "/admin/price-import": PriceImportScreen,
  "/admin/logo-import": LogoImportScreen,
  "/admin/design-import": DesignImportScreen,
  "/admin/import-status": ImportStatusScreen,
  "/admin/system-health": SystemHealthScreen,
  "/admin/co2": Co2CalculatorScreen,
  "/admin/orders-queue": OrdersQueueScreen,
  "/admin/prepayments": PrepaymentsScreen,
  "/admin/billing-overview": BillingOverviewScreen,
  "/admin/vendor-users": VendorUsersScreen,
  "/admin/stripe-payouts": StripePayoutsScreen,
  "/admin/nav-price-fallback": NavPriceFallbackScreen,
  "/admin/mail-test": MailTestScreen,
  "/admin/mail-delivery": MailDeliveryScreen,
  "/admin/approval-mails": ApprovalMailsScreen,
  "/admin/methods": MethodsScreen,
  "/admin/stock-orders": StockOrdersScreen,
  "/admin/api-keys": ApiKeysListScreen,
  "/admin/sheet-size": SheetSizeScreen,
});
import { RouteErrorView } from "./presentation/components/error-view/RouteErrorView";

/**
 * MSAL should be instantiated outside of the component tree to prevent it from being re-instantiated on re-renders.
 * For more, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/getting-started.md
 */
const msalInstance = new PublicClientApplication(msalConfig);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      // Fire requests regardless of the browser's online heuristic. With the
      // default "online" mode a brief network flap (wifi/VPN blip) flips
      // react-query's internal online flag and PAUSES every new query —
      // observed in the field 11/7-2026 as detail popups spinning forever
      // (status loading / fetchStatus paused) until a full page reload. The
      // app has no offline mode, so a real outage should surface as a normal
      // request error instead of an invisible freeze.
      networkMode: "always",
    },
    mutations: {
      // Same reasoning: a save must fail loudly, never hang silently.
      networkMode: "always",
    },
  },
});

// The standard route table — every host except the configured migration hosts
// uses EXACTLY this, untouched (empty/unmatched REACT_APP_MIGRATION_HOSTS =>
// byte-identical routing to before).
const standardRoutes = [
  {
    path: "/",
    element: <RootLayout />,
    errorElement: <RouteErrorView />,
    hasErrorBoundary: true,
    children: [
      {
        path: "",
        element: <PrivateView />,
        errorElement: <RouteErrorView />,
        hasErrorBoundary: true,
        children: [
          {
            path: "",
            element: <HomeRedirector />,
          },
          {
            path: "design",
            element: <DesignListScreen />,
          },
          {
            path: "style",
            element: (
              <AuthRoute
                requiredRole={Role.Admin}
                element={<StyleListScreen />}
              />
            ),
          },
          {
            path: "style/:styleId/edit",
            element: (
              <AuthRoute
                requiredRole={Role.Admin}
                element={<StyleEditScreen />}
              />
            ),
          },
          {
            path: "style/catalog",
            element: (
              <AuthRoute
                requiredRole={Role.Admin}
                element={<StyleCatalogUploadScreen />}
              />
            ),
          },
          {
            path: "order",
            element: <OrderListScreen />,
          },
          {
            path: "order/confirm",
            element: <CartRoute element={<OrderConfirmationScreen />} />,
            errorElement: <OrderConfirmErrorRedirect />,
          },
          {
            path: "order/end",
            element: <OrderPlaced />,
          },
          {
            path: "order/details",
            // Order details render per-line prices and totals, so hidden-price
            // customers must bounce here just like order/confirm and order/place.
            element: <CartRoute allowWorkshop element={<OrderDetailsScreen />} />,
          },
          {
            path: "order/place",
            element: <CartRoute element={<OrderCreateScreen />} />,
          },
          {
            path: "design/create",
            element: <CreateDesignScreen />,
          },
          {
            path: "design/:designId/changestyle",
            element: <DesignChangeStyleScreen />,
          },
          {
            path: "design/:designId/editor",
            element: <DesignEditor2Screen />,
          },
          {
            path: "account/me",
            element: <MyProfileScreen />,
          },
          {
            path: "style/:styleId/annotate",
            element: (
              <AuthRoute
                requiredRole={Role.Admin}
                element={<MaskEditorScreen />}
              />
            ),
          },
          {
            path: "customer",
            element: <CustomerListScreen />,
          },
          {
            path: "vendor",
            element: <VendorListScreen />,
          },
          {
            path: "logo",
            element: <LogoListScreen />,
          },
          {
            path: "logo/create",
            element: <LogoEditorScreen />,
          },
          {
            path: "customer/:customerId/edit",
            element: (
              <AuthRoute
                requiredRole={Role.Vendor}
                element={<CustomerUpdateScreen />}
              />
            ),
          },
          {
            path: "vendor/:vendorId/edit",
            element: (
              <AuthRoute
                requiredRole={Role.Vendor}
                element={<VendorUpdateScreen />}
              />
            ),
          },
          {
            path: "customer/:customerId",
            element: (
              <AuthRoute
                requiredRole={Role.Vendor}
                element={<CustomerDetailsScreen />}
              />
            ),
          },
          {
            path: "vendor/:vendorId",
            element: (
              <AuthRoute
                requiredRole={Role.Admin}
                element={<VendorDetailsScreen />}
              />
            ),
          },
          {
            path: "manufacturer",
            element: (
              <AuthRoute
                requiredRole={Role.Admin}
                element={<ManufacturerListScreen />}
              />
            ),
          },
          {
            path: "manufacturer/:manufacturerId",
            element: (
              <AuthRoute
                requiredRole={Role.Admin}
                element={<ManufacturerDetailsScreen />}
              />
            ),
          },
          {
            path: "customer/add",
            element: (
              <AuthRoute
                requiredRole={Role.Vendor}
                element={<CustomerCreateScreen />}
              />
            ),
          },
          {
            path: "admin/price-import",
            element: (
              <AuthRoute
                requiredRole={Role.Admin}
                element={<PriceImportScreen />}
              />
            ),
          },
          {
            path: "admin/statistics",
            element: (
              <AuthRoute
                requiredRole={Role.Admin}
                element={<StatisticsScreen />}
              />
            ),
          },
          {
            path: "admin/logo-import",
            element: (
              <AuthRoute
                requiredRole={Role.Admin}
                element={<LogoImportScreen />}
              />
            ),
          },
          {
            path: "admin/design-import",
            element: (
              <AuthRoute
                requiredRole={Role.Admin}
                element={<DesignImportScreen />}
              />
            ),
          },
          {
            path: "admin/co2",
            element: (
              <AuthRoute
                requiredRole={Role.Admin}
                element={<Co2CalculatorScreen />}
              />
            ),
          },
          {
            path: "admin/orders-queue",
            element: (
              <AuthRoute
                requiredRole={Role.Admin}
                element={<OrdersQueueScreen />}
              />
            ),
          },
          {
            path: "admin/methods",
            element: (
              <AuthRoute
                requiredRole={Role.Admin}
                element={<MethodsScreen />}
              />
            ),
          },
          {
            path: "admin/stock-orders",
            element: (
              <AuthRoute
                requiredRole={Role.Admin}
                element={<StockOrdersScreen />}
              />
            ),
          },
          {
            path: "admin/prepayments",
            element: (
              <AuthRoute
                requiredRole={Role.Admin}
                element={<PrepaymentsScreen />}
              />
            ),
          },
          {
            path: "admin/orders-today",
            element: (
              <AuthRoute
                requiredRole={Role.Admin}
                element={<OrdersTodayScreen />}
              />
            ),
          },
          {
            path: "admin/import-status",
            element: (
              <AuthRoute
                requiredRole={Role.Admin}
                element={<ImportStatusScreen />}
              />
            ),
          },
          {
            path: "admin/system-health",
            element: (
              <AuthRoute
                requiredRole={Role.Admin}
                element={<SystemHealthScreen />}
              />
            ),
          },
          {
            path: "admin/billing-overview",
            element: (
              <AuthRoute
                requiredRole={Role.Admin}
                element={<BillingOverviewScreen />}
              />
            ),
          },
          {
            path: "admin/vendor-users",
            element: (
              <AuthRoute
                requiredRole={Role.Admin}
                element={<VendorUsersScreen />}
              />
            ),
          },
          // Stripe payouts (flag REACT_APP_STRIPE_PAYOUTS_ENABLED): with the
          // flag off the routes do not exist at all (404 view), so the
          // bundle behaves exactly as today.
          ...(enviroment.stripePayoutsEnabled
            ? [
                {
                  path: "admin/stripe-payouts",
                  element: (
                    <AuthRoute
                      requiredRole={Role.Admin}
                      element={<StripePayoutsScreen />}
                    />
                  ),
                },
                {
                  path: "admin/stripe-payouts/:payoutId",
                  element: (
                    <AuthRoute
                      requiredRole={Role.Admin}
                      element={<StripePayoutDetailScreen />}
                    />
                  ),
                },
              ]
            : []),
          // NAV price fallback (flag REACT_APP_NAV_PRICE_FALLBACK_ADMIN_ENABLED):
          // with the flag off the route does not exist at all (404 view), so
          // the bundle behaves exactly as today.
          ...(enviroment.navPriceFallbackAdminEnabled
            ? [
                {
                  path: "admin/nav-price-fallback",
                  element: (
                    <AuthRoute
                      requiredRole={Role.Admin}
                      element={<NavPriceFallbackScreen />}
                    />
                  ),
                },
              ]
            : []),
          // Sheet size admin page (flag
          // REACT_APP_OVERSIZE_SHEET_ADMIN_PAGE_ENABLED): with the flag off
          // the route does not exist at all (404 view), so the bundle
          // behaves exactly as today.
          ...(enviroment.oversizeSheetAdminPageEnabled
            ? [
                {
                  path: "admin/sheet-size",
                  element: (
                    <AuthRoute
                      requiredRole={Role.Admin}
                      element={<SheetSizeScreen />}
                    />
                  ),
                },
              ]
            : []),
          // Mail test (flag REACT_APP_MAIL_TEST_ENABLED): with the flag off
          // the route does not exist at all (404 view), so the bundle
          // behaves exactly as today.
          ...(enviroment.mailTestEnabled
            ? [
                {
                  path: "admin/mail-test",
                  element: (
                    <AuthRoute
                      requiredRole={Role.Admin}
                      element={<MailTestScreen />}
                    />
                  ),
                },
              ]
            : []),
          // Mail delivery (flag REACT_APP_MAIL_DELIVERY_ADMIN_ENABLED): with
          // the flag off the route does not exist at all (404 view), so the
          // bundle behaves exactly as today.
          ...(enviroment.mailDeliveryAdminEnabled
            ? [
                {
                  path: "admin/mail-delivery",
                  element: (
                    <AuthRoute
                      requiredRole={Role.Admin}
                      element={<MailDeliveryScreen />}
                    />
                  ),
                },
              ]
            : []),
          // Approval mails statistics (flag
          // REACT_APP_APPROVAL_MAIL_STATS_ENABLED): with the flag off the
          // route does not exist at all (404 view), so the bundle behaves
          // exactly as today.
          ...(enviroment.approvalMailStatsEnabled
            ? [
                {
                  path: "admin/approval-mails",
                  element: (
                    <AuthRoute
                      requiredRole={Role.Admin}
                      element={<ApprovalMailsScreen />}
                    />
                  ),
                },
              ]
            : []),
          {
            path: "admin/api-keys",
            element: (
              <AuthRoute
                requiredRole={Role.Admin}
                element={<ApiKeysListScreen />}
              />
            ),
          },
          {
            path: "admin/api-keys/:id",
            element: (
              <AuthRoute
                requiredRole={Role.Admin}
                element={<ApiKeyDetailScreen />}
              />
            ),
          },
        ],
      },
      {
        path: "organization-disabled",
        element: <OrganizationDisabledScreen />,
      },
      {
        path: "public/design/external-editor",
        element: <ExternalDesignEditorScreen />,
      },
      {
        path: "public/style/external-upload",
        element: <ExternalStyleUploadScreen />,
      },
      {
        path: "public/velkommen",
        element: <VelkommenPage />,
      },
      // Short, shareable alias for the same page (js.jetsport.dk/velkommen).
      // The canonical public/velkommen path above stays untouched.
      {
        path: "velkommen",
        element: <VelkommenPage />,
      },
      {
        path: "public",
        element: <PublicView />,
        children: [
          {
            path: "design/approval/:designId",
            element: <ExternalApprovalDesignScreen />,
          },
          {
            path: "logo/approval/:logoId",
            element: <ExternalApprovalLogoScreen />,
          },
          {
            path: "logo/open/:logoId",
            element: <ExternalApprovalLogoScreen readOnly={true} />,
          },
          {
            path: "design/open/:designId",
            element: <ExternalApprovalDesignScreen />,
          },
          {
            path: "order/entry/:entryKey",
            element: <ExternalCustomerDesignEditor />,
          },
          {
            path: "migration",
            element: <MigrationPage />,
          },
          {
            path: "proof-pdf",
            element: <ProofPdfDownloadScreen />,
          },
        ],
      },
      {
        path: "approval",
        element: <PublicView />,
        children: [
          {
            path: "",
            element: <MultiApprovalRouter />,
          },
        ],
      },
    ],
  },
];

// Migration-host route table (webshop2.jetsport.dk): the old-webshop
// lookalike (MigrationPage) IS the front page — the root route renders it
// directly (public, no MSAL guard, no redirect), and every other path lands
// there too. Only ever used when window.location.hostname matches
// REACT_APP_MIGRATION_HOSTS (see isMigrationHost); all other hosts use
// standardRoutes above.
const migrationHostRoutes = [
  {
    path: "/",
    element: <RootLayout />,
    errorElement: <RouteErrorView />,
    hasErrorBoundary: true,
    children: [
      {
        path: "",
        element: <PublicView />,
        children: [
          {
            index: true,
            element: <MigrationPage />,
          },
          // Keep the canonical path working on this host too (shared links).
          {
            path: "public/migration",
            element: <MigrationPage />,
          },
        ],
      },
      // Velkommen (the "why KitMaker" page) is reachable on this host as well,
      // on both the short and the canonical path. Listed BEFORE the catch-all
      // below so it wins; every other path still falls through to the
      // lookalike exactly as before.
      {
        path: "velkommen",
        element: <VelkommenPage />,
      },
      {
        path: "public/velkommen",
        element: <VelkommenPage />,
      },
      // Catch-all: any other path on the migration host shows the lookalike
      // too. Rendered directly (no redirect): a navigation into the lazy
      // MigrationPage chunk suspends synchronously (react-router updates via
      // useSyncExternalStore) and throws React #426 / blanks the app, while
      // an initial-load suspension under RootLayout's <Suspense> is fine.
      {
        path: "*",
        element: <PublicView />,
        children: [
          {
            path: "*",
            element: <MigrationPage />,
          },
        ],
      },
    ],
  },
];

const router = createBrowserRouter(
  isMigrationHost() ? migrationHostRoutes : standardRoutes,
);

function RootLayout() {
  usePageViewTracking();
  // Flag REACT_APP_CLARITY_CONSENT_ENABLED: stored consent + route guard +
  // page-bound Clarity tags on every pathname change. Off = returns at once.
  useClarityConsentPageView();
  return (
    <Suspense fallback={<LoadingElement />}>
      <Outlet />
    </Suspense>
  );
}

// The one real change vs. the old useEffect+navigate() is push→REPLACE: the
// push left a dead "/" entry that made Back bounce straight forward again.
// (<Navigate> still commits a null frame and navigates from an effect
// internally - do not copy it expecting those to be gone.)
function HomeRedirector() {
  return <Navigate to="/logo" replace />;
}

const WATCHDOG_TIMEOUT_MS = 20000;
const WATCHDOG_GUARD_KEY = "msalStartupWatchdogReloaded";

/**
 * Splash watchdog: if MSAL is stuck in a non-interactive boot state
 * (Startup / HandleRedirect / auto-triggered Login) for more than
 * WATCHDOG_TIMEOUT_MS - seen when B2C hangs for 90+ seconds - do exactly ONE
 * window.location.reload(). The sessionStorage guard makes a reload loop
 * impossible: the next reload requires the app to have been healthy
 * (inProgress=None with an account) in between.
 */
function MsalStartupWatchdog(): null {
  const { inProgress, accounts } = useMsal();

  useEffect(() => {
    if (inProgress === InteractionStatus.None) {
      if (accounts.length > 0) {
        sessionStorage.removeItem(WATCHDOG_GUARD_KEY); // healthy again -> re-arm
      }
      return;
    }

    const stuckStates: InteractionStatus[] = [
      InteractionStatus.Startup,
      InteractionStatus.HandleRedirect,
      InteractionStatus.Login,
      // Logout dækkes nu af BootSplash (App.tsx) - et B2C-hæng på vejen UD
      // ville ellers ligne en evig, pæn splash. Reloaden er sikker: MSAL har
      // ryddet den lokale session før navigationen, og JUST_LOGGED_OUT-
      // markøren er sat ved kliket, så en genstart lander på logget ud-
      // skærmen i stedet for at logge brugeren ind igen.
      InteractionStatus.Logout,
    ];
    if (!stuckStates.includes(inProgress)) return;

    const timer = setTimeout(() => {
      if (sessionStorage.getItem(WATCHDOG_GUARD_KEY)) return; // only ever once
      sessionStorage.setItem(WATCHDOG_GUARD_KEY, String(Date.now()));
      window.location.reload();
    }, WATCHDOG_TIMEOUT_MS);

    return () => clearTimeout(timer); // any state change resets the clock
  }, [inProgress, accounts.length]);

  return null;
}

function AppWrapper(): JSX.Element {
  return <RouterProvider router={router} />;
}

const rootElement = document.getElementById("root");

// Flag REACT_APP_LIST_PERF_ENABLED (Q8, font-swap CLS): the fallback font
// stack in index.css is keyed on <html data-list-perf>, so the flag-off
// build ships the rule but never matches it (base div/button rules
// untouched). Set before the first render so no frame is styled twice.
if (enviroment.listPerfEnabled) {
  document.documentElement.dataset.listPerf = "1";
}

// Same flag: MUI text (Typography, buttons) gets the same metric-matched
// stack as the div/button rule in index.css, so it keeps its box during the
// font swap too. Off = today's bare "Inter".
const MUI_FONT_FAMILY = enviroment.listPerfEnabled
  ? 'Inter, "Inter Fallback", system-ui, -apple-system, "Segoe UI", Arial, sans-serif'
  : "Inter";

const theme = createTheme({
  typography: {
    fontFamily: MUI_FONT_FAMILY,
    fontSize: 10,
  },
  palette: {
    primary: {
      main: "#596675",
    },
  },
  components: {
    MuiPopover: {
      defaultProps: {
        container: rootElement,
      },
    },
    MuiPopper: {
      defaultProps: {
        container: rootElement,
      },
    },
    MuiDialog: {
      defaultProps: {
        container: rootElement,
      },
    },
    MuiModal: {
      defaultProps: {
        container: rootElement,
      },
    },
    MuiCssBaseline: {
      styleOverrides: `
        @font-face {
          font-family: 'Inter';
          font-style: normal;
          font-display: swap;
          font-weight: 400;
        }
      `,
    },
  },
});

const root = ReactDOM.createRoot(rootElement as HTMLElement);

root.render(
  <React.StrictMode>
    <MsalProvider instance={msalInstance}>
      <MsalStartupWatchdog />
      <QueryClientProvider client={queryClient}>
        <StyledEngineProvider injectFirst>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <AppWrapper />
            <ToastContainer />
          </ThemeProvider>
        </StyledEngineProvider>
      </QueryClientProvider>
    </MsalProvider>
  </React.StrictMode>
);

// Flag REACT_APP_WEB_VITALS_ENABLED (Q5): field LCP / INP / CLS as the GA4
// event "web_vitals" + Clarity tag cwv_daarlig (utils/analytics/webVitals.ts,
// through the consent-aware facades). The library is one small lazy chunk
// imported on idle AFTER the first render - web-vitals reads the buffered
// performance entries, so the early paint/shift entries are not missed.
// Off = today's argument-less call = no-op, no chunk.
if (enviroment.webVitalsEnabled) {
  scheduleWebVitals(() => reportWebVitals(reportWebVital));
} else {
  reportWebVitals();
}
// .then(() => console.log('No error'))
// .catch(() => console.log('Error'))
